import React, { useState, useEffect } from "react";
import { DatePicker, Select, Button, Table, Modal, message } from "antd";
import dayjs from "dayjs";
import {
  useFetchAttendanceReport,
  useFetchEmployeeOptions,
} from "../../AttendanceReport/hooks/useFetch";
import { formatMinutes, ShiftTag, StatusTags, InOutCell, onReportHeaderCell } from "../../AttendanceReport/utils";

// รายงานการเข้า-ออกงาน (รายเดือน) — สรุปต่อพนักงาน กดขยายดูรายวันทั้งเดือน
const AttendanceReportMonthlyPage = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [dailyRows, setDailyRows] = useState([]);
  const [breakDetail, setBreakDetail] = useState(null);

  const { data: employeeOptions = [], isLoading: isLoadingEmployees } =
    useFetchEmployeeOptions();
  const { mutateAsync: fetchReport, isLoading } = useFetchAttendanceReport();

  const handleSearch = async (
    month = selectedMonth,
    userId = selectedUserId,
    jobType = selectedJobType,
    shift = selectedShift
  ) => {
    try {
      const { result } = await fetchReport({
        startDate: month.startOf("month").format("YYYY-MM-DD"),
        endDate: month.endOf("month").format("YYYY-MM-DD"),
        userId: userId || undefined,
        jobType: jobType || undefined,
        shift: shift || undefined,
      });
      setDailyRows(result || []);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "ดึงรายงานการเข้า-ออกงานไม่สำเร็จ"
      );
    }
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // รวมรายวัน → สรุปต่อพนักงาน
  const summaryRows = [];
  const byUser = new Map();
  for (const r of dailyRows) {
    if (!byUser.has(r.userId)) {
      byUser.set(r.userId, {
        userId: r.userId,
        userName: r.userName,
        shift: r.shift,
        shiftName: r.shiftName,
        shiftTime: r.shiftTime,
        workDays: 0,
        absentDays: 0,
        holidayDays: 0,
        leaveDays: 0,
        lateCount: 0,
        noOutCount: 0,
        jobType: r.jobType,
        workMinutes: 0,
        breakMinutes: 0,
        breakNetMinutes: 0,
        breakOverDays: 0,
        lateShiftDays: 0,
        caseCount: 0,
        idleMinutes: 0,
        idleCaseCount: 0,
        earlyStopDays: 0,
        days: [],
      });
      summaryRows.push(byUser.get(r.userId));
    }
    const s = byUser.get(r.userId);
    s.days.push(r);
    // วันหยุด/ลา ไม่ใช่ทั้ง "มาทำงาน" และ "ขาดงาน" — แยกออกมาต่างหาก
    if (r.status === "holiday") s.holidayDays += 1;
    else if (r.status === "leave") s.leaveDays += 1;
    else if (r.status === "absent") s.absentDays += 1;
    else {
      s.workDays += 1;
      if (r.lateMinutes > 0) s.lateCount += 1;
      if (r.lateFromShift > 0) s.lateShiftDays += 1;
      if (r.breakOverMinutes > 0) s.breakOverDays += 1;
      if (r.status === "no_out") s.noOutCount += 1;
      s.workMinutes += r.workMinutes || 0;
      s.breakMinutes += r.breakMinutes || 0;
      s.breakNetMinutes += r.breakMinutesNet || 0;
      // ===== ฝั่ง Panel เท่านั้น (ฝั่ง Input ไม่มีการกด Start) =====
      s.caseCount += r.caseCount || 0;
      if (r.idleMinutes != null) {
        s.idleMinutes += r.idleMinutes;
        s.idleCaseCount += r.caseCount || 0;
      }
      // ทำเคสสุดท้ายจบก่อนตอกออกเกิน 1 ชม. — ดูเป็น "รูปแบบ" ต้องนับหลายวันถึงจะมีความหมาย
      if (r.idleBeforeOutMinutes >= 60) s.earlyStopDays += 1;
    }
  }

  // แปลงยอดรวมเป็น "อัตราส่วน" — ยอดรวมเทียบกันไม่ได้เพราะแต่ละคนมาทำงานไม่เท่ากัน
  for (const s of summaryRows) {
    s.casePerDay = s.workDays ? Math.round(s.caseCount / s.workDays) : 0;
    s.idleAvgPerCase = s.idleCaseCount
      ? Math.round((s.idleMinutes / s.idleCaseCount) * 100) / 100
      : 0;
    s.breakNetPerDay = s.workDays ? Math.round(s.breakNetMinutes / s.workDays) : 0;
    s.workMinutesPerDay = s.workDays ? Math.round(s.workMinutes / s.workDays) : 0;
  }

  // ค่าเฉลี่ยทีม (เฉพาะ Panel ที่มีเคสจริง) — ไว้เป็นเส้นเทียบ ไม่งั้นตัวเลขรายคนอ่านแล้วไม่รู้ว่าดีหรือแย่
  const panelRows = summaryRows.filter((r) => r.jobType === "2" && r.caseCount > 0);
  const teamAvg = panelRows.length
    ? {
        casePerDay: Math.round(
          panelRows.reduce((a, r) => a + r.casePerDay, 0) / panelRows.length
        ),
        idleAvgPerCase:
          Math.round(
            (panelRows.reduce((a, r) => a + r.idleMinutes, 0) /
              Math.max(1, panelRows.reduce((a, r) => a + r.idleCaseCount, 0))) * 100
          ) / 100,
        breakNetPerDay: Math.round(
          panelRows.reduce((a, r) => a + r.breakNetPerDay, 0) / panelRows.length
        ),
        // ถ่วงน้ำหนักด้วยจำนวนวัน ไม่ใช่เฉลี่ยของค่าเฉลี่ย — คนมา 3 วันจะได้ไม่ดึงค่าทีมเท่าคนมา 20 วัน
        workMinutesPerDay: Math.round(
          panelRows.reduce((a, r) => a + r.workMinutes, 0) /
            Math.max(1, panelRows.reduce((a, r) => a + r.workDays, 0))
        ),
      }
    : null;

  // แย่กว่าค่าเฉลี่ยทีมมาก = ควรสะดุดตา (ใช้ 1.5 เท่าเป็นเกณฑ์ กันคนที่เกินนิดหน่อยโดนเหมารวม)
  const worseThanTeam = (value, avg) => avg > 0 && value > avg * 1.5;

  const columns = [
    {
      title: "พนักงาน",
      dataIndex: "userName",
      key: "userName",
      onHeaderCell: onReportHeaderCell,
      render: (name) => <span style={{ fontWeight: 600 }}>{name}</span>,
      sorter: (a, b) => a.userName.localeCompare(b.userName),
    },
    {
      title: "กะ",
      key: "shift",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => <ShiftTag shift={r.shift} shiftName={r.shiftName} shiftTime={r.shiftTime} />,
    },
    {
      title: "มาทำงาน",
      dataIndex: "workDays",
      key: "workDays",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v) => `${v} วัน`,
      sorter: (a, b) => a.workDays - b.workDays,
    },
    {
      title: "ไม่ได้ตอก",
      dataIndex: "absentDays",
      key: "absentDays",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v) =>
        v > 0 ? <span style={{ color: "#f5222d", fontWeight: 600 }}>{v} วัน</span> : "-",
      sorter: (a, b) => a.absentDays - b.absentDays,
    },
    {
      title: "วันหยุด",
      dataIndex: "holidayDays",
      key: "holidayDays",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v) => (v > 0 ? <span style={{ color: "#1677ff" }}>{v} วัน</span> : "-"),
      sorter: (a, b) => a.holidayDays - b.holidayDays,
    },
    {
      title: "ลา",
      dataIndex: "leaveDays",
      key: "leaveDays",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v) => (v > 0 ? <span style={{ color: "#722ed1" }}>{v} วัน</span> : "-"),
      sorter: (a, b) => a.leaveDays - b.leaveDays,
    },
    {
      // ความหมายของ "สาย" เปลี่ยนไปแล้ว (รวมพักเกินสิทธิ์) → แยกที่มาให้เห็น
      // ไม่งั้นคนมาตรงเวลาทุกวันแต่ขึ้นสาย จะงงว่าทำไม
      title: "มาสาย",
      dataIndex: "lateCount",
      key: "lateCount",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v, r) =>
        v > 0 ? (
          <div>
            <span style={{ color: "#fa541c", fontWeight: 600 }}>{v} วัน</span>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {r.lateShiftDays > 0 && `เข้าสาย ${r.lateShiftDays}`}
              {r.lateShiftDays > 0 && r.breakOverDays > 0 && " · "}
              {r.breakOverDays > 0 && `พักเกิน ${r.breakOverDays}`}
            </div>
          </div>
        ) : (
          "-"
        ),
      sorter: (a, b) => a.lateCount - b.lateCount,
    },
    {
      title: "ลืมตอกออก",
      dataIndex: "noOutCount",
      key: "noOutCount",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v) => (v > 0 ? `${v} ครั้ง` : "-"),
    },
    {
      title: "ชม.ทำงานรวม (หักพัก)",
      dataIndex: "workMinutes",
      key: "workMinutes",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v) => <span style={{ fontWeight: 600 }}>{formatMinutes(v)}</span>,
      sorter: (a, b) => a.workMinutes - b.workMinutes,
    },
    {
      // ยอดรวมเทียบกันไม่ได้เพราะแต่ละคนมาทำงานไม่เท่ากัน — ต้องมีค่าเฉลี่ยต่อวันคู่กัน
      title: "ชม.ทำงาน / วัน",
      dataIndex: "workMinutesPerDay",
      key: "workMinutesPerDay",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v, r) => (
        <div>
          <span style={{ fontWeight: 600 }}>{formatMinutes(v)}</span>
          {teamAvg?.workMinutesPerDay > 0 && (
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              ทีม {formatMinutes(teamAvg.workMinutesPerDay)}
            </div>
          )}
        </div>
      ),
      sorter: (a, b) => a.workMinutesPerDay - b.workMinutesPerDay,
    },
    {
      title: "พักสุทธิ / วัน",
      dataIndex: "breakNetPerDay",
      key: "breakNetPerDay",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v, r) => (
        <div>
          <span
            style={{
              fontWeight: 600,
              color: worseThanTeam(v, teamAvg?.breakNetPerDay) ? "#cf1322" : undefined,
            }}
          >
            {formatMinutes(v)}
          </span>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            รวม {formatMinutes(r.breakMinutes)}
          </div>
        </div>
      ),
      sorter: (a, b) => a.breakNetPerDay - b.breakNetPerDay,
    },
    {
      // ===== 3 คอลัมน์นี้มีเฉพาะ Panel(Output) =====
      title: "เคส / วัน",
      dataIndex: "casePerDay",
      key: "casePerDay",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v, r) =>
        r.caseCount > 0 ? (
          <div>
            <span style={{ fontWeight: 600 }}>{v}</span>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>รวม {r.caseCount}</div>
          </div>
        ) : (
          <span style={{ color: "#bbb" }}>-</span>
        ),
      sorter: (a, b) => a.casePerDay - b.casePerDay,
    },
    {
      title: "ว่างเฉลี่ย / เคส",
      dataIndex: "idleAvgPerCase",
      key: "idleAvgPerCase",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v, r) =>
        r.caseCount > 0 ? (
          <span
            style={{
              fontWeight: 600,
              color: worseThanTeam(v, teamAvg?.idleAvgPerCase) ? "#cf1322" : undefined,
            }}
          >
            {v} น.
          </span>
        ) : (
          <span style={{ color: "#bbb" }}>-</span>
        ),
      sorter: (a, b) => a.idleAvgPerCase - b.idleAvgPerCase,
    },
    {
      title: "จบงานก่อน ≥1 ชม.",
      dataIndex: "earlyStopDays",
      key: "earlyStopDays",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v, r) =>
        r.caseCount > 0 ? (
          v > 0 ? (
            <span style={{ color: v >= r.workDays / 2 ? "#cf1322" : "#fa8c16", fontWeight: 600 }}>
              {v}/{r.workDays} วัน
            </span>
          ) : (
            "-"
          )
        ) : (
          <span style={{ color: "#bbb" }}>-</span>
        ),
      sorter: (a, b) => a.earlyStopDays - b.earlyStopDays,
    },
  ];

  // ตารางรายวันข้างใน (ตอนกดขยายแถวพนักงาน)
  const dailyColumns = [
    {
      title: "วันที่",
      dataIndex: "date",
      key: "date",
      onHeaderCell: onReportHeaderCell,
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    { title: "เข้า → ออก", key: "inout", align: "center", onHeaderCell: onReportHeaderCell, render: (_, r) => <InOutCell record={r} /> },
    {
      title: "ชม.ทำงาน (หักพัก)",
      key: "workMinutes",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => formatMinutes(r.workMinutes),
    },
    {
      title: "พัก",
      key: "breakMinutes",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) =>
        r.breakCount > 0 ? (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setBreakDetail(r)}>
            {formatMinutes(r.breakMinutes)} ({r.breakCount} รอบ)
          </Button>
        ) : r.status === "absent" ? (
          "-"
        ) : (
          <span style={{ color: "#8c8c8c" }}>ไม่มีพัก</span>
        ),
    },
    { title: "สถานะ", key: "status", align: "center", onHeaderCell: onReportHeaderCell, render: (_, r) => <StatusTags record={r} /> },
  ];

  return (
    <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh", marginLeft: 16 }}>
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            margin: 0,
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#212121",
            letterSpacing: "0.5px",
            lineHeight: 1.2,
          }}
        >
          รายงานการเข้า-ออกงาน (รายเดือน)
        </h1>
        <p style={{ margin: "8px 0 0", color: "#666" }}>
          สรุปการเข้า-ออกงานต่อพนักงานทั้งเดือน กดที่แถวเพื่อดูรายวัน
        </p>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <div className="flex gap-3 items-center flex-wrap">
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(m) => {
              if (!m) return;
              setSelectedMonth(m);
              handleSearch(m, selectedUserId, selectedJobType, selectedShift);
            }}
            format="MM/YYYY"
            allowClear={false}
            disabledDate={(d) => d.isAfter(dayjs(), "month")}
          />
          <Select
            className="w-40"
            placeholder="ตำแหน่งทั้งหมด"
            allowClear
            options={[
              { value: "1", label: "Admin(Input)" },
              { value: "2", label: "Panel(Output)" },
            ]}
            value={selectedJobType}
            onChange={(v) => {
              setSelectedJobType(v || null);
              handleSearch(selectedMonth, selectedUserId, v || null, selectedShift);
            }}
          />
          <Select
            className="w-32"
            placeholder="กะทั้งหมด"
            allowClear
            options={[
              // ต้องตรงกับ SHIFT_CONFIG ฝั่ง backend (BackEnd/src/utils/shiftUtils.js)
              { value: "1", label: "All Day" },
              { value: "2", label: "Day" },
              { value: "3", label: "Night" },
              { value: "4", label: "Afternoon" },
            ]}
            value={selectedShift}
            onChange={(v) => {
              setSelectedShift(v || null);
              handleSearch(selectedMonth, selectedUserId, selectedJobType, v || null);
            }}
          />
          <Select
            className="w-64"
            placeholder="พนักงานทั้งหมด"
            allowClear
            showSearch
            optionFilterProp="label"
            loading={isLoadingEmployees}
            options={employeeOptions}
            value={selectedUserId}
            onChange={(v) => {
              setSelectedUserId(v || null);
              handleSearch(selectedMonth, v || null, selectedJobType, selectedShift);
            }}
          />
          <Button type="primary" loading={isLoading} onClick={() => handleSearch()}>
            ค้นหา
          </Button>
        </div>
      </div>

      {/* เส้นเทียบของทีม — ตัวเลขรายคนอ่านแล้วไม่รู้ว่าดีหรือแย่ ถ้าไม่มีค่ากลางให้เทียบ
          คนที่แย่กว่าค่าเฉลี่ยเกิน 1.5 เท่า จะขึ้นสีแดงในตาราง */}
      {teamAvg && (
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 12,
            display: "flex",
            gap: 32,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: 600, color: "#595959" }}>
            ค่าเฉลี่ยทีม Panel ({panelRows.length} คน)
          </span>
          <span>
            เคส/วัน <strong>{teamAvg.casePerDay}</strong>
          </span>
          <span>
            ว่างเฉลี่ย/เคส <strong>{teamAvg.idleAvgPerCase} น.</strong>
          </span>
          <span>
            พักสุทธิ/วัน <strong>{formatMinutes(teamAvg.breakNetPerDay)}</strong>
          </span>
          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
            สีแดง = แย่กว่าค่าเฉลี่ยเกิน 1.5 เท่า
          </span>
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 10, padding: 16 }}>
        <Table
          rowKey="userId"
          loading={isLoading}
          dataSource={summaryRows}
          columns={columns}
          bordered
          scroll={{ x: "max-content" }}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                rowKey={(r) => `${r.userId}-${r.date}`}
                dataSource={record.days}
                columns={dailyColumns}
                pagination={false}
                size="small"
                bordered
                rowClassName={(r) => (r.status === "absent" ? "opacity-60" : "")}
              />
            ),
          }}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (t) => `ทั้งหมด ${t} คน`,
          }}
        />
      </div>
      <style>{`.ant-table-tbody > tr:hover > td { background-color: #e8f4f8 !important; }`}</style>

      {/* รายละเอียดพัก */}
      <Modal
        title={
          breakDetail
            ? `รายละเอียดพัก — ${breakDetail.userName} (${dayjs(breakDetail.date).format("DD/MM/YYYY")})`
            : ""
        }
        open={!!breakDetail}
        onCancel={() => setBreakDetail(null)}
        footer={null}
      >
        {breakDetail && (
          <>
            <p style={{ marginBottom: 12 }}>
              พักรวม <strong>{formatMinutes(breakDetail.breakMinutes)}</strong> ({breakDetail.breakCount} รอบ)
            </p>
            <Table
              columns={[
                { title: "รอบที่", key: "no", render: (_, __, i) => i + 1, width: 70 },
                { title: "ออฟไลน์", dataIndex: "start", key: "start", render: (v) => v.slice(11, 19) },
                { title: "กลับมาออนไลน์", dataIndex: "end", key: "end", render: (v) => v.slice(11, 19) },
                { title: "ระยะเวลา", dataIndex: "minutes", key: "minutes", render: (m) => formatMinutes(m) },
              ]}
              dataSource={breakDetail.breaks}
              rowKey={(b) => b.start}
              pagination={false}
              size="small"
              bordered
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default AttendanceReportMonthlyPage;
